import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send } from 'lucide-react';
import { getFirstName } from '../utils/nameUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    fetchConversation();
    fetchMessages();
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`);
      setConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      // Set default conversation
      setConversation({
        id: conversationId,
        name: 'User',
        photo: 'https://i.pravatar.cc/150?img=10'
      });
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Set empty messages array
      setMessages([]);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: `msg_${Date.now()}`,
      sender: 'current_user',
      text: newMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages([...messages, message]);
    setNewMessage('');
    
    try {
      await axios.post(`${API}/conversations/${conversationId}/messages`, message);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (!conversation) return <div style={{ padding: '20px', color: '#E6D7C3' }}>Loading...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#2C1B13' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #A57C33' }}>
        <button onClick={() => navigate('/messages')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D7B56D', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <img src={conversation.photo} alt={conversation.name} style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid #A57C33' }} />
        <div>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#E6D7C3', margin: 0 }}>{getFirstName(conversation.name)}</h1>
          <p style={{ fontSize: '12px', color: '#A57C33', margin: 0 }}>Online</p>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', paddingBottom: '100px' }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              justifyContent: msg.sender === 'current_user' ? 'flex-end' : 'flex-start',
              marginBottom: '16px'
            }}
          >
            <div
              style={{
                maxWidth: '70%',
                padding: '12px 16px',
                borderRadius: '16px',
                background: msg.sender === 'current_user' 
                  ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
                  : 'rgba(230, 215, 195, 0.1)',
                color: msg.sender === 'current_user' ? '#2C1B13' : '#E6D7C3',
                border: msg.sender === 'current_user' ? 'none' : '1px solid #A57C33'
              }}
            >
              <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>{msg.text}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '10px', opacity: 0.7 }}>
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: 0,
        right: 0,
        padding: '16px',
        background: '#2C1B13',
        borderTop: '1px solid #A57C33'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="input-field"
            style={{ flex: 1, margin: 0 }}
          />
          <button
            onClick={handleSend}
            className="btn-primary"
            style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;